/* [PATCH pomoc-fe v1] Gumb Pomoč v glavi + desni panel s postopki po korakih + namig ob prvem obisku strani.
   NACRT_pomoc_uporabnikom_v3_20261015. Vsebina: ./vsebina/<aplikacija>.ts; izbira: ./izbira.ts.
   Gumb je viden samo na straneh, za katere obstaja vsebina (faza 1: Prodaja; [PATCH pomoc-fe v2] + Nabava, Banka, Potni nalogi, Delovni čas). */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useAplikacija } from '../../hooks/useAplikacija';
import { usePnVloga } from '../../lib/pnVloga'; // [PATCH pomoc-fe v2] vloga admin v PN/DČ (enako kot meni)
import { izberiStran, jeVideno, oznaciVideno } from './izbira';

/* **besedilo** -> poudarjeno */
function Besedilo({ t }: { t: string }) {
  const deli = t.split('**');
  return (
    <>
      {deli.map((d, i) => (i % 2 === 1
        ? <b key={i} className="font-semibold text-gray-900 dark:text-white">{d}</b>
        : <React.Fragment key={i}>{d}</React.Fragment>))}
    </>
  );
}

const UVOD = '_uvod'; // [PATCH pomoc-fe v2b] en sam »videno« za namig v vseh aplikacijah

export function PomocGumb() {
  const location = useLocation();
  const { aplikacija } = useAplikacija();
  const vloga = useAuthStore((s: any) => s.role);
  const pnVloga = usePnVloga(); // [PATCH pomoc-fe v2]
  const stran = izberiStran(aplikacija, location.pathname, location.search, vloga === 'stranka', pnVloga === 'admin');
  const [odprt, setOdprt] = useState(false);
  const [namig, setNamig] = useState(false);
  const [razprt, setRazprt] = useState<string>('');

  const kljuc = stran?.kljuc || '';
  useEffect(() => {
    setRazprt('');
    setNamig(!!kljuc && !jeVideno(UVOD)); /* [PATCH pomoc-fe v2b] namig samo enkrat, ne po straneh */
  }, [kljuc]);

  useEffect(() => {
    if (!odprt) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOdprt(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [odprt]);

  if (!stran) return null;

  const odpri = () => { oznaciVideno(UVOD); setNamig(false); setOdprt(true); };
  const zapriNamig = () => { oznaciVideno(UVOD); setNamig(false); };

  return (
    <div className="relative">
      <button onClick={odpri} title="Pomoč — navodila za to stran"
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400">
        <HelpCircle className="w-5 h-5" />
      </button>

      {namig && !odprt && (
        <div className="absolute right-0 top-full mt-2 w-64 z-50 rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900 shadow-lg p-3 text-sm text-gray-700 dark:text-gray-200">
          <div className="font-medium mb-1">Potrebuješ pomoč?</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">Na vsaki strani najdeš navodila po korakih pod ikono vprašaja zgoraj desno.</div>
          <div className="flex justify-end gap-2">
            <button onClick={zapriNamig} className="px-2.5 py-1 rounded-lg text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">Ne zdaj</button>
            <button onClick={odpri} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700">Pokaži navodila</button>
          </div>
        </div>
      )}

      {odprt && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setOdprt(false)} />
          <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
              <div>
                <div className="text-xs uppercase tracking-wide text-blue-600 font-semibold">Pomoč</div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{stran.naslov}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1"><Besedilo t={stran.opis} /></p>
              </div>
              <button onClick={() => setOdprt(false)} title="Zapri (Esc)"
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {stran.postopki.map((p) => {
                const odp = razprt === p.id;
                return (
                  <div key={p.id} className="rounded-xl border border-gray-200 dark:border-gray-800">
                    <button onClick={() => setRazprt(odp ? '' : p.id)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800/60 rounded-xl">
                      {odp ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                      <span className="flex-1">{p.naslov}</span>
                      {p.za !== 'vsi' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500">{p.za === 'admin' ? 'admin' : 'računovodja'}</span>}{/* [PATCH pomoc-fe v2] */}
                    </button>
                    {odp && (
                      <ol className="px-3 pb-3 pt-1 space-y-2">
                        {p.koraki.map((k, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-gray-600 dark:text-gray-300">
                            <span className="shrink-0 w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                            <span><Besedilo t={k} /></span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                );
              })}

              {(stran.tezave || []).length > 0 && (
                <div className="pt-3">
                  <h3 className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-2">Pogoste težave</h3>
                  <div className="space-y-2">
                    {(stran.tezave || []).map((t, i) => (
                      <div key={i} className="rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm">
                        <div className="font-medium text-gray-800 dark:text-gray-100">{t.vprasanje}</div>
                        <div className="text-gray-600 dark:text-gray-300"><Besedilo t={t.odgovor} /></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 text-xs text-gray-400">
              Ne najdeš odgovora? Uporabi oddaljeno pomoč (ISL) ali se obrni na svoje računovodstvo.
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
